import { defineComponent, PropType } from 'vue';

import { Currency_Service } from "../../../system/services/currency";
import { product_search_product } from '../../objects/v1_3/product/search/product_search_product';
import { Booking } from '../../services/booking';
import { booking_group } from '../../objects/v1_2/Booking/Booking_Group';
import { passenger } from '../../objects/v1_2/Booking/Passenger';
import { trip_passenger } from '../../objects/v1_2/Booking/Trip_Passenger';
import { trip } from '../../objects/v1_2/Booking/Trip';
import { booking_information } from '../../objects/v1_2/Booking/Booking_Information';
import { Payment } from '../../services/payment';
import { response } from "../../objects/v1_3/payment/initialise/response";
import { request_payment_initialise_internal } from '../../objects/v1_3/payment/initialise/request_payment_initialise_internal';
import { booking_search_group } from '../../objects/v1_2/Booking_Details/booking_search_group';
import { request_payment_url } from '../../objects/v1_3/payment/initialise/request_payment_url';

const Book: Booking = new Booking();
const Pay: Payment = new Payment();

const Currency_Services: Currency_Service = new Currency_Service();

const FALLBACK_DENOMINATIONS: number[] = [25, 50, 75, 100, 150, 200, 250, 500];

export default defineComponent({
    name: "gift_card",
    components: {},
    props: {
        product: {  
            type: Object as PropType<product_search_product>,
            required: true
        }
    },
    emits: ["close"],
    data() {
        return {
            recipient_email: "" as string,
            recipient_email_confirm: "" as string,
            first_name: "" as string,
            last_name: "" as string,
            selected_denomination: null as number | null,
            custom_amount_raw: "" as string,
            is_custom: false as boolean,
            errors: {} as Record<string, string>,
            submitted: false as boolean,
            bookingGroup: new booking_group(),
            is_processing : false as boolean,
            return_booking: null as any,
            return_loading: false as boolean,

            order: {
                tour_id: "" as string,
                first_name: "" as string,
                last_name: "" as string,
                recipient_email: "" as string,
                denomination: null as number | null,
                currency: "" as string,
            }
        };
    },
    computed: {
        return_paid(): boolean {
            if (!this.return_booking) return false;
            return (this.return_booking.trips?.[0]?.passengers?.[0]?.outstanding ?? 1) === 0;
        },
        denominations(): number[] {
            if (this.product?.prices?.length) {
                const mapped = this.product.prices
                    .map((p: any) => p.price ?? p.amount ?? p.value ?? 0)
                    .filter((v: number) => v > 0)
                    .sort((a: number, b: number) => a - b);
                if (mapped.length) return mapped;
            }
            return FALLBACK_DENOMINATIONS;
        },
        selected_index(): number {
            if (this.is_custom || this.selected_denomination === null) return -1;
            return this.denominations.indexOf(this.selected_denomination);
        },
        can_go_prev(): boolean {
            if (this.is_custom) return false;
            return this.selected_index > 0;
        },
        can_go_next(): boolean {
            if (this.is_custom) return false;
            return this.selected_index < this.denominations.length - 1;
        },
        custom_amount_value(): number {
            const parsed = parseFloat(this.custom_amount_raw);
            return isNaN(parsed) ? 0 : parsed;
        },
        effective_amount(): number | null {
            if (this.is_custom) return this.custom_amount_value > 0 ? this.custom_amount_value : null;
            return this.selected_denomination;
        },
        formatted_amount(): string {
            if (this.is_custom) {
                if (this.custom_amount_value > 0)
                    return Currency_Services.Format_Currency(this.custom_amount_value, this.$store.getters.get_Currency);
                return "—";
            }
            if (this.selected_denomination === null) return "—";
            return Currency_Services.Format_Currency(this.selected_denomination, this.$store.getters.get_Currency);
        },
        currency_code(): string {
            return this.$store.getters.get_Currency?.currency_info?.iso_currency_code ?? "GBP";
        },
        currency_symbol(): string {
            return this.$store.getters.get_Currency?.currency_info?.currency_symbol ?? "£";
        },
        card_title(): string {
            return this.product?.name_short || this.product?.name || "Gift Card";
        },
        card_description(): string {
            return this.product?.description_headline || this.product?.description_short || "";
        },
        card_image(): string {
            return this.product?.media_asset?.asset_url ?? "";
        },
        is_valid(): boolean {
            return Object.keys(this.validate_form()).length === 0;
        },
        
    },
    methods: {
        get_return_email(): string {
            return this.return_booking?.passengers?.[0]?.email ?? "";
        },
        get_return_amount(): string {
            const raw = this.return_booking?.trips?.[0]?.passengers?.[0]?.price;
            if (!raw) return "";
            return Currency_Services.Format_Currency(raw, this.$store.getters.get_Currency);
        },
        format_currency(amount: number): string {
            return Currency_Services.Format_Currency(amount, this.$store.getters.get_Currency);
        },

        select_denomination(amount: number): void {
            this.is_custom = false;
            this.selected_denomination = amount;
            this.custom_amount_raw = "";
            if (this.errors.amount) delete this.errors.amount;
        },

        enter_custom(): void {
            this.is_custom = true;
            this.selected_denomination = null;
            this.$nextTick(() => {
                (this.$refs.custom_input as HTMLInputElement)?.focus();
            });
        },

        on_custom_input(): void {
            if (this.errors.amount) delete this.errors.amount;
        },

        step_denomination(direction: 1 | -1): void {
            const list = this.denominations;
            if (!list.length) return;
            if (!this.selected_denomination) {
                this.selected_denomination = list[0];
                return;
            }
            const idx = list.indexOf(this.selected_denomination);
            const next = idx + direction;
            if (next >= 0 && next < list.length) {
                this.selected_denomination = list[next];
                if (this.errors.amount) delete this.errors.amount;
            }
        },

        validate_form(): Record<string, string> {
            const errs: Record<string, string> = {};

            if (!this.effective_amount || this.effective_amount <= 0)
                errs.amount = "Please select or enter a gift card value.";
            else if (this.effective_amount < 5)
                errs.amount = "Minimum gift card value is £5.";
            else if (this.effective_amount > 2000)
                errs.amount = "Maximum gift card value is £2,000.";

            if (!this.first_name.trim())
                errs.first_name = "First name is required.";

            if (!this.last_name.trim())
                errs.last_name = "Last name is required.";

            if (!this.recipient_email.trim())
                errs.email = "Your email is required.";
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.recipient_email))
                errs.email = "Please enter a valid email address.";

            if (!this.recipient_email_confirm.trim())
                errs.email_confirm = "Please confirm your email.";
            else if (this.recipient_email.toLowerCase() !== this.recipient_email_confirm.toLowerCase())
                errs.email_confirm = "Email addresses do not match.";

            return errs;
        },

        async proceed(): Promise<void> {
            this.submitted = true;
            this.errors = this.validate_form();
            if (!this.is_valid) return;

            this.is_processing = true;

            this.order = {
                tour_id: this.product.tour_id,
                first_name: this.first_name.trim(),
                last_name: this.last_name.trim(),
                recipient_email: this.recipient_email.trim(),
                denomination: this.effective_amount,
                currency: this.currency_code,
            };

            this.bookingGroup.passengers[0].email = this.recipient_email.trim();
            this.bookingGroup.passengers[0].first_name = this.first_name.trim();
            this.bookingGroup.passengers[0].family_name = this.last_name.trim();

            let tripPassenger: trip_passenger = new trip_passenger();
            tripPassenger.price = this.effective_amount ?? 0;
            tripPassenger.index = 0;

            this.bookingGroup.trips[0].passengers.push(tripPassenger);

            let booking_info = await Book.Book_Trip_From_Booking_Group(this.bookingGroup).then(response => { return response.json(); });
            this.Payment(booking_info);
        },
        Get_Payment_App_Id(): string {
            let payment_app_id : any = import.meta.env.VITE_PAYMENT_APP_ID; //default
            if (this.$store.getters.get_Payment_APP_ID != null && this.$store.getters.get_Payment_APP_ID != '') {
                payment_app_id = this.$store.getters.get_Payment_APP_ID; //set by paylink
            }
            return payment_app_id;
        },
        async Payment(booking_info: booking_search_group) {
            let payment_session : request_payment_initialise_internal = new request_payment_initialise_internal();
            let custom_urls: request_payment_url = new request_payment_url();  
            
            custom_urls.url_success = window.location.origin 
                + "/?view=giftcard&status=success"
                + "&ref=" + encodeURIComponent(booking_info.group_booking_id ?? "");

            custom_urls.url_cancellation = window.location.origin + "/?view=giftcard&status=cancelled";
            custom_urls.url_failure = window.location.origin + "/?view=giftcard&status=failed";
            

            let Payment_Response: response = await Pay.Payment_Initialise_Custom_URLS(true, false, booking_info, payment_session, this.$store.getters.get_Agent_Login, true, false, this.Get_Payment_App_Id(), custom_urls).then(response => { return response.json(); })
            
            window.location.href = Payment_Response.payment_methods[0].form_iframe ?? "";
        },
        close(): void {
            this.$emit("close");
        },

        has_error(field: string): boolean {
            return this.submitted && !!this.errors[field];
        },
        async get_quote(): Promise<void> {
            let giftCardBooking: booking_group = new booking_group();
            let passengerObj: passenger = new passenger();
            let bookingInfo: booking_information = new booking_information();

            bookingInfo.iso_currency_code = this.currency_code;
            giftCardBooking.booking_information = bookingInfo;

            passengerObj.age_category = "Adult";
            giftCardBooking.passengers[0] = passengerObj;

            giftCardBooking.trips[0] = new trip();
            giftCardBooking.trips[0].departure_date = new Date().toISOString().split('T')[0].split('-').map(p => parseInt(p).toString()).join('/');
            giftCardBooking.trips[0].passengers = [];
            giftCardBooking.trips[0].tour_id = this.product.tour_id ?? "";
            giftCardBooking.trips[0].promo_code = "";

            await Book.Quote_Booking_From_Booking_Group(giftCardBooking);
            this.bookingGroup = giftCardBooking;
        },
        page_status(): string {
            return (this.$route.query.status as string) ?? "";
        },
        get_transaction_ref(): string {
            return (this.$route.query.ref as string) ?? "";
        },
    },
    mounted() {
        if (this.$route.query.ref) {
            this.return_loading = true;
            Book.Get_Book(
                this.$route.query.ref as string,
                this.$store.getters.get_Agent_Login
            ).then((info: any) => {
                this.return_booking = info;
                this.return_loading = false;
            });
        }
        this.selected_denomination = 100;
        document.body.style.overflow = 'hidden';
    },
    unmounted() {
        document.body.style.overflow = '';
    },
    watch: {
        effective_amount(val: number | null) {
            if (val) this.get_quote();
        }
    },
});